// ⚠️  DOC-UPDATE: UI changes here may require updates to:
//     • client/public/crafting-guide.html (crafting UI tabs, workflows)
import React, { useState, useEffect, useCallback } from 'react';
import './CraftingPanel.css';
import { getRarityColor, getRarityClass, getRarityGlow, getRarityLabel } from '../utils/rarityUtils';
import { formatCurrency } from './shared/formatCompact';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// Helper: normalize magic from object {0:{...},1:{...}} to array [{...},{...}]
const normalizeMagic = (magic) => {
  if (!magic) return [];
  if (Array.isArray(magic)) return magic;
  if (typeof magic === 'object') return Object.values(magic);
  return [];
};

const QUALITY_COLORS = {
  Crude: '#888',
  Standard: '#ccc',
  Fine: '#4fc3f7',
  Superior: '#ab47bc',
  Masterwork: '#ffd700',
};

const DISCIPLINE_INFO = {
  smithing: { icon: '⚒️', label: 'Smithing', stat: 'STR' },
  alchemy: { icon: '⚗️', label: 'Alchemy', stat: 'MEN' },
  enchanting: { icon: '🔮', label: 'Enchanting', stat: 'MEN' },
};

const CraftingPanel = ({ character, onClose, onCharacterUpdate }) => {
  const [mainTab, setMainTab] = useState('craft'); // 'craft', 'salvage', 'socket', 'enhance', 'scrolls'
  const [discipline, setDiscipline] = useState('smithing');
  const [recipes, setRecipes] = useState([]);
  const [craftingStatus, setCraftingStatus] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [message, setMessage] = useState({ text: '', type: 'info' });
  const [loading, setLoading] = useState(true);
  const [crafting, setCrafting] = useState(false);
  const [lastCraftResult, setLastCraftResult] = useState(null);
  const [playerGold, setPlayerGold] = useState(character?.gold || 0);
  // v643.4 (B2/B3): recipe filter + search
  const [recipeSearch, setRecipeSearch] = useState('');
  const [showOnlyCraftable, setShowOnlyCraftable] = useState(false);

  // Salvage state
  const [salvageItems, setSalvageItems] = useState([]);
  const [salvageLoading, setSalvageLoading] = useState(false);
  const [selectedSalvageItem, setSelectedSalvageItem] = useState(null);
  const [salvaging, setSalvaging] = useState(false);
  const [lastSalvageResult, setLastSalvageResult] = useState(null);
  const [showSalvageConfirm, setShowSalvageConfirm] = useState(false);

  // Socket state
  const [socketData, setSocketData] = useState({ items: [], gems: [], enchantingLevel: 0 });
  const [socketLoading, setSocketLoading] = useState(false);
  const [selectedSocketItem, setSelectedSocketItem] = useState(null);
  const [selectedGem, setSelectedGem] = useState(null);
  const [socketing, setSocketing] = useState(false);
  const [lastSocketResult, setLastSocketResult] = useState(null);

  // Enhance state
  const [enhanceData, setEnhanceData] = useState({ items: [], smithingLevel: 0, gold: 0 });
  const [enhanceLoading, setEnhanceLoading] = useState(false);
  const [selectedEnhanceItem, setSelectedEnhanceItem] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [lastEnhanceResult, setLastEnhanceResult] = useState(null);

  // Scroll state
  const [scrollData, setScrollData] = useState({ scrolls: [], items: [], enchantingLevel: 0 });
  const [scrollLoading, setScrollLoading] = useState(false);
  const [selectedScroll, setSelectedScroll] = useState(null);
  const [selectedScrollTarget, setSelectedScrollTarget] = useState(null);
  const [applyingScroll, setApplyingScroll] = useState(false);
  const [lastScrollResult, setLastScrollResult] = useState(null);

  const token = localStorage.getItem('token');

  // Badge: does the player have any enchantment scrolls ready to apply?
  const hasEnchantScrolls = (character?.inventory || []).some(
    item => item.type === 'scroll' && item.spell && item.spell.startsWith('enchant_')
  );

  // Load crafting status on mount
  useEffect(() => {
    loadStatus();
  }, []);

  // Sync gold from character prop
  useEffect(() => {
    if (character) {
      setPlayerGold(character.gold || 0);
    }
  }, [character]);

  // Reload recipes when discipline changes
  useEffect(() => {
    if (mainTab === 'craft') {
      loadRecipes();
      setSelectedRecipe(null);
      setLastCraftResult(null);
    }
  }, [discipline, mainTab]);

  // Keep selectedRecipe in sync when recipes refresh (e.g. after crafting)
  // so the detail panel shows updated material counts
  useEffect(() => {
    if (selectedRecipe && recipes.length > 0) {
      const updated = recipes.find(r => r.id === selectedRecipe.id);
      if (updated) {
        setSelectedRecipe(updated);
      }
    }
  }, [recipes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load salvageable items when switching to salvage tab
  useEffect(() => {
    if (mainTab === 'salvage') {
      loadSalvageableItems();
      setSelectedSalvageItem(null);
      setLastSalvageResult(null);
    }
  }, [mainTab]);

  // Load socket data when switching to socket tab
  useEffect(() => {
    if (mainTab === 'socket') {
      loadSocketData();
      setSelectedSocketItem(null);
      setSelectedGem(null);
      setLastSocketResult(null);
    }
  }, [mainTab]);

  // Load enhance data when switching to enhance tab
  useEffect(() => {
    if (mainTab === 'enhance') {
      loadEnhanceData();
      setSelectedEnhanceItem(null);
      setLastEnhanceResult(null);
    }
  }, [mainTab]);

  // Load scroll data when switching to scrolls tab
  useEffect(() => {
    if (mainTab === 'scrolls') {
      loadScrollData();
      setSelectedScroll(null);
      setSelectedScrollTarget(null);
      setLastScrollResult(null);
    }
  }, [mainTab]);

  const loadStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/crafting/status`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setCraftingStatus(data.crafting);
      }
    } catch (error) {
      console.error('Error loading crafting status:', error);
    }
  };

  const loadRecipes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/crafting/recipes?discipline=${discipline}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setRecipes(data.recipes || []);
        if (craftingStatus) {
          setCraftingStatus(prev => ({
            ...prev,
            [discipline]: {
              ...prev?.[discipline],
              level: data.craftingLevel,
            }
          }));
        }
      } else {
        setMessage({ text: 'Failed to load recipes', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading recipes:', error);
      setMessage({ text: 'Error connecting to server', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadSalvageableItems = async () => {
    try {
      setSalvageLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/crafting/salvageable`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setSalvageItems(data.items || []);
      } else {
        setMessage({ text: 'Failed to load inventory', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading salvageable items:', error);
      setMessage({ text: 'Error connecting to server', type: 'error' });
    } finally {
      setSalvageLoading(false);
    }
  };

  const loadSocketData = async () => {
    try {
      setSocketLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/crafting/socketable`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setSocketData(data);
      } else {
        setMessage({ text: 'Failed to load socket data', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading socket data:', error);
    } finally {
      setSocketLoading(false);
    }
  };

  const loadEnhanceData = async () => {
    try {
      setEnhanceLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/crafting/enhanceable`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setEnhanceData(data);
        setPlayerGold(data.gold || 0);
      } else {
        setMessage({ text: 'Failed to load enhance data', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading enhance data:', error);
    } finally {
      setEnhanceLoading(false);
    }
  };

  const loadScrollData = async () => {
    try {
      setScrollLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/crafting/scrolls`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setScrollData(data);
      } else {
        setMessage({ text: 'Failed to load scroll data', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading scroll data:', error);
    } finally {
      setScrollLoading(false);
    }
  };

  const handleCraft = useCallback(async () => {
    if (!selectedRecipe || crafting) return;

    setCrafting(true);
    setLastCraftResult(null);
    setMessage({ text: 'Crafting...', type: 'info' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/crafting/craft`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipeId: selectedRecipe.id })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        // v643.4 (B4): specific guidance for inventory-full failures so
        // players know no materials were consumed and what to do next.
        if (err.code === 'INVENTORY_FULL') {
          setMessage({ text: '🎒 Inventory full — free a slot and try again. No materials were consumed.', type: 'error' });
        } else if (response.status === 429) {
          setMessage({ text: err.error || 'Slow down — too many crafting actions in a short window.', type: 'error' });
        } else {
          setMessage({ text: err.error || `Crafting failed (${response.status})`, type: 'error' });
        }
        return;
      }

      const data = await response.json();

      if (data.success) {
        let msg = data.message;
        if (data.autoLearned && data.autoLearned.length > 0) {
          msg += ` 📜 New recipes learned!`;
        }
        setMessage({ text: msg, type: 'success' });
        setLastCraftResult(data);
        setPlayerGold(data.gold);
        // Reload recipes to update material counts
        await loadRecipes();
        await loadStatus();
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Crafting failed', type: 'error' });
      }
    } catch (error) {
      console.error('Error crafting:', error);
      setMessage({ text: 'Error processing craft', type: 'error' });
    } finally {
      setCrafting(false);
    }
  }, [selectedRecipe, crafting, token, onCharacterUpdate]);

  const handleSalvage = useCallback(async () => {
    if (!selectedSalvageItem || salvaging) return;

    setShowSalvageConfirm(false);
    setSalvaging(true);
    setLastSalvageResult(null);
    setMessage({ text: 'Salvaging...', type: 'info' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/crafting/salvage`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ inventoryIndex: selectedSalvageItem.index })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setMessage({ text: err.error || `Salvage failed (${response.status})`, type: 'error' });
        return;
      }

      const data = await response.json();

      if (data.success) {
        let msg = data.message;
        if (data.autoLearned && data.autoLearned.length > 0) {
          msg += ` 📜 New recipes learned!`;
        }
        if (data.levelUp) {
          const discLabel = data.discipline === 'alchemy' ? 'Alchemy' : 'Smithing';
          msg += ` 🎉 ${discLabel} leveled up to ${data.newLevel}!`;
        }
        setMessage({ text: msg, type: 'success' });
        setLastSalvageResult(data);
        setSelectedSalvageItem(null);
        await loadSalvageableItems();
        await loadStatus();
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Salvage failed', type: 'error' });
      }
    } catch (error) {
      console.error('Error salvaging:', error);
      setMessage({ text: 'Error processing salvage', type: 'error' });
    } finally {
      setSalvaging(false);
    }
  }, [selectedSalvageItem, salvaging, token, onCharacterUpdate]);

  // v639.3: Bulk salvage by rarity
  const handleBulkSalvage = useCallback(async (maxRarity) => {
    if (salvaging) return;
    const tierLabel = { common: 'Common', uncommon: 'Common + Uncommon', rare: 'Common + Uncommon + Rare' }[maxRarity] || maxRarity;
    // v643.4 (B6): pre-count eligible items so the confirm dialog tells
    // players how many things they're about to break down.
    const rarityRank = { common: 1, uncommon: 2, rare: 3, epic: 4, legendary: 5, mythic: 6 };
    const cap = rarityRank[maxRarity] || 1;
    const eligible = (salvageItems || []).filter(it => {
      if (it.locked) return false;
      if (it.type === 'quest' || it.droppable === false) return false;
      const r = rarityRank[(it.rarity || 'common').toLowerCase()] || 1;
      return r <= cap;
    });
    const eligibleCount = eligible.length;
    if (eligibleCount === 0) {
      setMessage({ text: `No ${tierLabel} gear in your inventory to salvage.`, type: 'info' });
      return;
    }
    if (!window.confirm(`Salvage ${eligibleCount} ${tierLabel} item${eligibleCount === 1 ? '' : 's'} from your inventory?\n\n⭐ Locked items and quest items are skipped. Equipped gear is not affected.`)) return;
    setSalvaging(true);
    setLastSalvageResult(null);
    setMessage({ text: 'Bulk salvaging...', type: 'info' });
    try {
      const response = await fetch(`${API_BASE_URL}/api/crafting/salvage-bulk`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ maxRarity }),
      });
      const data = await response.json();
      if (data.success) {
        const matsList = Object.entries(data.materials || {}).map(([n, q]) => `${n} x${q}`).join(', ');
        let msg = `Salvaged ${data.salvagedCount} item${data.salvagedCount === 1 ? '' : 's'}.`;
        if (matsList) msg += ` Materials: ${matsList}.`;
        if (data.levelUps && data.levelUps.length > 0) {
          msg += ` 🎉 ${data.levelUps.map(l => `${l.discipline} → L${l.newLevel}`).join(', ')}!`;
        }
        if (data.discoveredRecipes && data.discoveredRecipes.length > 0) {
          msg += ` 📜 Discovered: ${data.discoveredRecipes.join(', ')}`;
        }
        setMessage({ text: msg, type: 'success' });
        setLastSalvageResult(data);
        setSelectedSalvageItem(null);
        await loadSalvageableItems();
        await loadStatus();
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Bulk salvage failed', type: 'error' });
      }
    } catch (err) {
      console.error('Bulk salvage error:', err);
      setMessage({ text: 'Bulk salvage failed', type: 'error' });
    } finally {
      setSalvaging(false);
    }
  }, [salvaging, salvageItems, token, onCharacterUpdate]);

  // v643.4 (B6): keep the bulk-salvage handler tied to the latest
  // salvageItems list so the eligible-count is always fresh.

  const handleSocket = useCallback(async () => {
    if (!selectedSocketItem || !selectedGem || socketing) return;

    setSocketing(true);
    setLastSocketResult(null);
    setMessage({ text: 'Socketing gem...', type: 'info' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/crafting/socket`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ equipSlot: selectedSocketItem.slot, gemInventoryIndex: selectedGem.index })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setMessage({ text: err.error || `Socketing failed (${response.status})`, type: 'error' });
        return;
      }

      const data = await response.json();

      if (data.success) {
        let msg = data.message;
        if (data.levelUp) msg += ` 🎉 Enchanting leveled up to ${data.newLevel}!`;
        setMessage({ text: msg, type: 'success' });
        setLastSocketResult(data);
        setSelectedGem(null);
        await loadSocketData();
        // Refresh selected socket item with updated data (socket count, etc.)
        const refreshResp = await fetch(`${API_BASE_URL}/api/crafting/socketable`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (refreshResp.ok) {
          const refreshed = await refreshResp.json();
          const updatedItem = refreshed.items?.find(i => i.slot === selectedSocketItem.slot);
          setSelectedSocketItem(updatedItem || null);
        }
        await loadStatus();
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Socketing failed', type: 'error' });
      }
    } catch (error) {
      console.error('Error socketing gem:', error);
      setMessage({ text: 'Error processing socket', type: 'error' });
    } finally {
      setSocketing(false);
    }
  }, [selectedSocketItem, selectedGem, socketing, token, onCharacterUpdate]);

  const handleEnhance = useCallback(async () => {
    if (!selectedEnhanceItem || enhancing) return;

    setEnhancing(true);
    setLastEnhanceResult(null);
    setMessage({ text: 'Enhancing...', type: 'info' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/crafting/enhance`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ equipSlot: selectedEnhanceItem.slot })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setMessage({ text: err.error || `Enhancement failed (${response.status})`, type: 'error' });
        return;
      }

      const data = await response.json();

      if (data.success) {
        setMessage({ text: data.message, type: data.succeeded ? 'success' : 'error' });
        setLastEnhanceResult(data);
        setPlayerGold(data.gold);
        // Reload enhance data and update selected item with fresh data
        const refreshResp = await fetch(`${API_BASE_URL}/api/crafting/enhanceable`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (refreshResp.ok) {
          const refreshed = await refreshResp.json();
          setEnhanceData(refreshed);
          setPlayerGold(refreshed.gold || 0);
          // Update selected item to match refreshed data so button text updates
          const updatedItem = refreshed.items.find(i => i.slot === selectedEnhanceItem.slot);
          setSelectedEnhanceItem(updatedItem || null);
        }
        await loadStatus();
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Enhancement failed', type: 'error' });
      }
    } catch (error) {
      console.error('Error enhancing item:', error);
      setMessage({ text: 'Error processing enhancement', type: 'error' });
    } finally {
      setEnhancing(false);
    }
  }, [selectedEnhanceItem, enhancing, token, onCharacterUpdate]);

  const handleApplyScroll = useCallback(async () => {
    if (!selectedScroll || !selectedScrollTarget || applyingScroll) return;

    setApplyingScroll(true);
    setLastScrollResult(null);
    setMessage({ text: 'Applying scroll...', type: 'info' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/crafting/apply-scroll`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scrollInventoryIndex: selectedScroll.index, equipSlot: selectedScrollTarget.slot })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setMessage({ text: err.error || `Scroll application failed (${response.status})`, type: 'error' });
        return;
      }

      const data = await response.json();

      if (data.success) {
        let msg = data.message;
        if (data.levelUp) msg += ` \ud83c\udf89 Enchanting leveled up to ${data.newLevel}!`;
        setMessage({ text: msg, type: 'success' });
        setLastScrollResult(data);
        setSelectedScroll(null);
        await loadScrollData();
        await loadStatus();
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Scroll application failed', type: 'error' });
      }
    } catch (error) {
      console.error('Error applying scroll:', error);
      setMessage({ text: 'Error applying scroll', type: 'error' });
    } finally {
      setApplyingScroll(false);
    }
  }, [selectedScroll, selectedScrollTarget, applyingScroll, token, onCharacterUpdate]);

  const getXpBarWidth = () => {
    if (!craftingStatus?.[discipline]) return 0;
    const { xpInLevel, xpNeeded } = craftingStatus[discipline];
    if (xpNeeded <= 0) return 100;
    return Math.min(100, Math.round((xpInLevel / xpNeeded) * 100));
  };

  const canCraftSelected = () => {
    if (!selectedRecipe) return false;
    if (!selectedRecipe.canCraft) return false;
    if (playerGold < selectedRecipe.goldCost) return false;
    return selectedRecipe.materials.every(m => m.enough);
  };

  return (
    <div className="crafting-overlay" onClick={onClose} tabIndex={-1}>
      <div className="crafting-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="crafting-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2>⚒️ Crafting Station</h2>
            <a
              href="/crafting-guide.html"
              target="_blank"
              rel="noopener noreferrer"
              title="Open Crafting Guide"
              style={{
                fontSize: '0.75rem',
                color: '#80b0ff',
                textDecoration: 'none',
                background: 'rgba(100,140,255,0.1)',
                border: '1px solid rgba(100,140,255,0.25)',
                borderRadius: '4px',
                padding: '2px 8px',
                marginTop: '2px',
                whiteSpace: 'nowrap',
                transition: 'background 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => { e.target.style.background = 'rgba(100,140,255,0.2)'; e.target.style.borderColor = 'rgba(100,140,255,0.5)'; }}
              onMouseLeave={e => { e.target.style.background = 'rgba(100,140,255,0.1)'; e.target.style.borderColor = 'rgba(100,140,255,0.25)'; }}
            >📖 Guide</a>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="crafting-gold">
              <span>💰</span>
              <span>{formatCurrency(playerGold)}</span>
            </div>
            <button className="crafting-close-btn" onClick={onClose} title="Close (Esc)">✖</button>
          </div>
        </div>

        {/* Main Tabs: Craft / Salvage / Socket / Enhance */}
        <div className="crafting-main-tabs">
          <button
            className={`crafting-main-tab ${mainTab === 'craft' ? 'active' : ''}`}
            onClick={() => setMainTab('craft')}
          >
            ⚒️ Craft
          </button>
          <button
            className={`crafting-main-tab ${mainTab === 'salvage' ? 'active' : ''}`}
            onClick={() => setMainTab('salvage')}
          >
            🔨 Salvage
          </button>
          <button
            className={`crafting-main-tab ${mainTab === 'socket' ? 'active' : ''}`}
            onClick={() => setMainTab('socket')}
          >
            💎 Socket
          </button>
          <button
            className={`crafting-main-tab ${mainTab === 'enhance' ? 'active' : ''}`}
            onClick={() => setMainTab('enhance')}
          >
            ⬆️ Enhance
          </button>
          <button
            className={`crafting-main-tab ${mainTab === 'scrolls' ? 'active' : ''}`}
            onClick={() => setMainTab('scrolls')}
          >
            📜 Scrolls
            {hasEnchantScrolls && mainTab !== 'scrolls' && (
              <span className="crafting-tab-notify">!</span>
            )}
          </button>
        </div>

        {/* ── CRAFT TAB ── */}
        {mainTab === 'craft' && (
          <>
            {/* Discipline Sub-Tabs */}
            <div className="crafting-tabs">
              {Object.entries(DISCIPLINE_INFO).map(([key, info]) => (
                <button
                  key={key}
                  className={`crafting-tab ${discipline === key ? 'active' : ''}`}
                  onClick={() => setDiscipline(key)}
                >
                  {info.icon} {info.label}
                  {craftingStatus?.[key] && (
                    <span className="crafting-tab-level">Lv {craftingStatus[key].level}</span>
                  )}
                </button>
              ))}
            </div>

            {/* XP Bar */}
            {craftingStatus?.[discipline] && (
              <div className="crafting-xp-bar-container">
                <div className="crafting-xp-label">
                  {DISCIPLINE_INFO[discipline].icon} {DISCIPLINE_INFO[discipline].label} Level {craftingStatus[discipline].level}
                  <span className="crafting-xp-text">
                    {craftingStatus[discipline].xpInLevel} / {craftingStatus[discipline].xpNeeded} XP
                  </span>
                </div>
                <div className="crafting-xp-bar">
                  <div
                    className="crafting-xp-fill"
                    style={{ width: `${getXpBarWidth()}%` }}
                  />
                </div>
              </div>
            )}

            {/* Main Content */}
            <div className="crafting-body">
              {/* Recipe List */}
              <div className="crafting-recipe-list">
                <div className="crafting-recipe-list-header">📜 Recipes</div>
                {/* v643.4 (B2/B3): search + can-craft filter */}
                <div className="crafting-recipe-filters">
                  <input
                    type="text"
                    className="crafting-recipe-search"
                    placeholder="🔍 Search recipes…"
                    value={recipeSearch}
                    onChange={(e) => setRecipeSearch(e.target.value)}
                  />
                  <label className="crafting-recipe-toggle" title="Hide recipes you can't craft right now">
                    <input
                      type="checkbox"
                      checked={showOnlyCraftable}
                      onChange={(e) => setShowOnlyCraftable(e.target.checked)}
                    />
                    <span>Craftable only</span>
                  </label>
                </div>
                {loading ? (
                  <div className="crafting-loading">Loading recipes...</div>
                ) : recipes.length === 0 ? (
                  <div className="crafting-empty">No recipes available</div>
                ) : (
                  <div className="crafting-recipe-scroll">
                    {(() => {
                      const q = recipeSearch.trim().toLowerCase();
                      const filtered = recipes.filter(r => {
                        if (q) {
                          const hay = `${r.name} ${r.output?.name || ''} ${r.output?.type || ''}`.toLowerCase();
                          if (!hay.includes(q)) return false;
                        }
                        if (showOnlyCraftable) {
                          if (!r.isKnown || r.locked) return false;
                          const allMats = r.materials.every(m => m.enough);
                          const canAfford = playerGold >= r.goldCost;
                          if (!allMats || !canAfford) return false;
                        }
                        return true;
                      });
                      if (filtered.length === 0) {
                        return <div className="crafting-empty">No recipes match these filters.</div>;
                      }
                      return filtered.map(recipe => {
                      const isSelected = selectedRecipe?.id === recipe.id;
                      const allMats = recipe.isKnown && recipe.materials.every(m => m.enough);
                      const canAfford = playerGold >= recipe.goldCost;

                      return (
                        <div
                          key={recipe.id}
                          className={`crafting-recipe-item ${isSelected ? 'selected' : ''} ${recipe.locked ? 'locked' : ''} ${!recipe.isKnown ? 'unknown' : ''}`}
                          onClick={() => recipe.isKnown && setSelectedRecipe(recipe)}
                          title={recipe.isKnown ? `${recipe.output?.description || recipe.name}\nXP: +${recipe.xpGain} | Gold: ${recipe.goldCost}` : `Recipe not yet discovered\n🔍 ${recipe.hint || 'Discover through adventuring'}`}
                        >
                          <div className="crafting-recipe-item-name">
                            {recipe.locked ? '🔒 ' : !recipe.isKnown ? '❓ ' : ''}{recipe.name}
                          </div>
                          <div className="crafting-recipe-item-meta">
                            {recipe.isKnown && !recipe.locked && recipe.output && (
                              <span className="crafting-recipe-type">
                                {recipe.output.type === 'weapon' ? '⚔️' : recipe.output.type === 'armor' ? '🛡️' : recipe.output.type === 'head' ? '⛑️' : recipe.output.type === 'accessory' || recipe.output.type === 'ring' ? '💍' : recipe.output.type === 'restore' ? '🧪' : recipe.output.type === 'scroll' ? '📜' : '📦'}
                              </span>
                            )}
                            <span className={`crafting-recipe-level ${recipe.locked ? 'locked' : ''}`}>
                              Lv {recipe.level}
                            </span>
                            {recipe.isKnown && !recipe.locked && (
                              <span className={`crafting-recipe-ready ${allMats && canAfford ? 'can-craft' : 'missing'}`}>
                                {allMats && canAfford ? '✓' : '✗'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                      });
                    })()}
                  </div>
                )}
              </div>

              {/* Recipe Detail */}
              <div className="crafting-detail">
                {selectedRecipe ? (
                  <>
                    <div className="crafting-detail-header">
                      <h3>{selectedRecipe.name}</h3>
                      <span className="crafting-detail-level">Craft Level {selectedRecipe.level}</span>
                    </div>

                    {/* Materials */}
                    <div className="crafting-detail-section">
                      <div className="crafting-section-title">Materials Required</div>
                      {selectedRecipe.materials.map((mat, idx) => (
                        <div key={idx} className={`crafting-material ${mat.enough ? 'have' : 'missing'}`}>
                          <span className="crafting-material-icon">{mat.enough ? '✅' : '❌'}</span>
                          <span className="crafting-material-name">{mat.name}</span>
                          <span className="crafting-material-qty">
                            {mat.have}/{mat.need}
                            {mat.haveStash > 0 && (
                              <span className="crafting-material-stash" title="Includes materials from your stash">
                                {' '}(🏠{mat.haveStash})
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                      <div className={`crafting-material ${playerGold >= selectedRecipe.goldCost ? 'have' : 'missing'}`}>
                        <span className="crafting-material-icon">{playerGold >= selectedRecipe.goldCost ? '✅' : '❌'}</span>
                        <span className="crafting-material-name">💰 Gold</span>
                        <span className="crafting-material-qty">
                          {formatCurrency(playerGold)}/{formatCurrency(selectedRecipe.goldCost)}
                        </span>
                      </div>
                    </div>

                    {/* Output Preview */}
                    <div className="crafting-detail-section">
                      <div className="crafting-section-title">Output</div>
                      <div className="crafting-output-preview">
                        <div className="crafting-output-name">{selectedRecipe.output.name}</div>
                        <div className="crafting-output-meta">
                          {selectedRecipe.output.type && (
                            <span className="craft-stat craft-stat-type">
                              {selectedRecipe.output.type === 'weapon' ? '⚔️ Weapon' : selectedRecipe.output.type === 'armor' ? '🛡️ Armor' : selectedRecipe.output.type === 'head' ? '⛑️ Head' : selectedRecipe.output.type === 'accessory' || selectedRecipe.output.type === 'ring' ? '💍 Accessory' : selectedRecipe.output.type === 'restore' ? '🧪 Consumable' : selectedRecipe.output.type === 'scroll' ? '📜 Scroll' : selectedRecipe.output.type}
                            </span>
                          )}
                          {selectedRecipe.output.level > 0 && (
                            <span className="craft-stat craft-stat-level">Req Lv {selectedRecipe.output.level}</span>
                          )}
                          {selectedRecipe.output.cost > 0 && (
                            <span className="craft-stat craft-stat-value">💰 {selectedRecipe.output.cost}g</span>
                          )}
                        </div>
                        <div className="crafting-output-stats">
                          {selectedRecipe.output.damage && (
                            <span className="craft-stat">⚔️ {selectedRecipe.output.damage}</span>
                          )}
                          {selectedRecipe.output.att && (
                            <span className="craft-stat">🎯 +{selectedRecipe.output.att} ATT</span>
                          )}
                          {selectedRecipe.output.bonus && (
                            <span className="craft-stat">🛡️ {selectedRecipe.output.bonus} DEF</span>
                          )}
                          {selectedRecipe.output.mdamage && (
                            <span className="craft-stat">✨ {selectedRecipe.output.mdamage} {selectedRecipe.output.mtype}</span>
                          )}
                          {selectedRecipe.output.hp > 0 && (
                            <span className="craft-stat">❤️ {selectedRecipe.output.hp} HP</span>
                          )}
                          {selectedRecipe.output.mp > 0 && (
                            <span className="craft-stat">💧 {selectedRecipe.output.mp} MP</span>
                          )}
                          {selectedRecipe.output.sp > 0 && (
                            <span className="craft-stat">⚡ {selectedRecipe.output.sp} SP</span>
                          )}
                          {selectedRecipe.output.magic?.map((m, i) => (
                            <span key={i} className="craft-stat craft-stat-magic">
                              🔮 +{m.mod} {m.type === 'ablup' ? (m.abl || '').toUpperCase() : m.type.toUpperCase()}
                            </span>
                          ))}
                        </div>
                        {selectedRecipe.output.description && (
                          <div className="crafting-output-desc">{selectedRecipe.output.description}</div>
                        )}
                        <div className="crafting-output-xp">+{selectedRecipe.xpGain} crafting XP</div>
                      </div>
                    </div>

                    {/* Craft Result */}
                    {lastCraftResult && (
                      <div className="crafting-result">
                        <div className="crafting-result-title">
                          <span style={{ color: QUALITY_COLORS[lastCraftResult.quality] || '#ccc' }}>
                            ★ {lastCraftResult.quality} Quality ★
                          </span>
                        </div>
                        <div className="crafting-result-roll">
                          Roll: {lastCraftResult.qualityRoll?.base}
                          {lastCraftResult.qualityRoll?.levelBonus > 0 && ` +${lastCraftResult.qualityRoll.levelBonus} level`}
                          {lastCraftResult.qualityRoll?.statBonus > 0 && ` +${lastCraftResult.qualityRoll.statBonus} stat`}
                          {' '}= {lastCraftResult.qualityRoll?.total}
                        </div>
                        {lastCraftResult.item && (
                          <div className="crafting-result-item" style={{ borderColor: getRarityColor(lastCraftResult.item) || '#444' }}>
                            <strong style={{ color: getRarityColor(lastCraftResult.item), textShadow: getRarityGlow(lastCraftResult.item) }}>{lastCraftResult.item.name}</strong>
                            {lastCraftResult.item.damage && <span> | ⚔️ {lastCraftResult.item.damage}</span>}
                            {lastCraftResult.item.bonus && <span> | 🛡️ {lastCraftResult.item.bonus}</span>}
                            {lastCraftResult.item.att && <span> | 🎯 +{lastCraftResult.item.att}</span>}
                            {lastCraftResult.item.hp > 0 && <span> | ❤️ {lastCraftResult.item.hp}</span>}
                            {lastCraftResult.item.mp > 0 && <span> | 💧 {lastCraftResult.item.mp}</span>}
                            {lastCraftResult.item.magic && (Array.isArray(lastCraftResult.item.magic) ? lastCraftResult.item.magic : Object.values(lastCraftResult.item.magic)).map((m, i) => (
                              <span key={i}> | 🔮 +{m.mod || m.bonus || 0} {m.type === 'ablup' ? (m.abl || '').toUpperCase() : m.type.toUpperCase()}</span>
                            ))}
                          </div>
                        )}
                        {lastCraftResult.levelUp && (
                          <div className="crafting-level-up">
                            🎉 Level Up! {DISCIPLINE_INFO[discipline].label} is now level {lastCraftResult.newLevel}!
                          </div>
                        )}
                      </div>
                    )}

                    {/* Craft Button */}
                    <button
                      className={`crafting-craft-btn ${canCraftSelected() ? 'ready' : 'disabled'}`}
                      disabled={!canCraftSelected() || crafting}
                      onClick={handleCraft}
                    >
                      {crafting ? '⏳ Crafting...' : `⚒️ Craft (${selectedRecipe.goldCost} gold)`}
                    </button>
                  </>
                ) : (
                  <div className="crafting-detail-empty">
                    <div className="crafting-detail-empty-icon">{DISCIPLINE_INFO[discipline].icon}</div>
                    <div>Select a recipe to begin crafting</div>
                    <div className="crafting-detail-empty-hint">
                      Quality depends on your {DISCIPLINE_INFO[discipline].stat} stat and crafting level
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── SALVAGE TAB ── */}
        {mainTab === 'salvage' && (
          <>
            {/* Salvage Info Bar */}
            <div className="crafting-xp-bar-container salvage-info-bar">
              <div className="crafting-xp-label">
                🔨 Salvage — Break down equipment into crafting materials
                <span className="crafting-xp-text">Awards Smithing / Alchemy XP</span>
              </div>
            </div>

            {/* v639.3: Bulk salvage controls */}
            <div style={{
              display: 'flex', gap: 8, padding: '6px 10px', margin: '0 0 6px',
              background: 'rgba(40,30,20,0.55)', border: '1px solid #5a4a2a',
              borderRadius: 4, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.85em'
            }}>
              <span style={{ color: '#c9a84c', fontWeight: 'bold' }}>🧹 Bulk salvage:</span>
              <button onClick={() => handleBulkSalvage('common')} disabled={salvaging}
                style={{ background: '#3a3a3a', color: '#bbb', border: '1px solid #9d9d9d', padding: '3px 10px', borderRadius: 3, cursor: 'pointer' }}
                title="Salvage every Common gear (skips locked, equipped, quest)">Common</button>
              <button onClick={() => handleBulkSalvage('uncommon')} disabled={salvaging}
                style={{ background: '#1a3a1a', color: '#1eff00', border: '1px solid #1eff00', padding: '3px 10px', borderRadius: 3, cursor: 'pointer' }}
                title="Salvage every Common + Uncommon gear">+ Uncommon</button>
              <button onClick={() => handleBulkSalvage('rare')} disabled={salvaging}
                style={{ background: '#1a2a3a', color: '#0070dd', border: '1px solid #0070dd', padding: '3px 10px', borderRadius: 3, cursor: 'pointer' }}
                title="Salvage every Common + Uncommon + Rare gear">+ Rare</button>
              <span style={{ color: '#888', fontSize: '0.85em', marginLeft: 'auto' }}>
                Lock (⭐) gear in inventory to keep it safe.
              </span>
            </div>

            {/* Salvage Body */}
            <div className="crafting-body">
              {/* Item List */}
              <div className="crafting-recipe-list">
                <div className="crafting-recipe-list-header">🎒 Equipment ({salvageItems.length})</div>
                {salvageLoading ? (
                  <div className="crafting-loading">Loading inventory...</div>
                ) : salvageItems.length === 0 ? (
                  <div className="crafting-empty">No salvageable items in inventory</div>
                ) : (
                  <div className="crafting-recipe-scroll">
                    {salvageItems.map((item) => {
                      const isSelected = selectedSalvageItem?.index === item.index;
                      const statBits = [];
                      if (item.damage) statBits.push(`Dmg ${item.damage}`);
                      if (item.att) statBits.push(`ATT +${item.att}`);
                      if (item.bonus) statBits.push(`DEF +${item.bonus}`);
                      if (item.dr) statBits.push(`DR ${item.dr}`);
                      const sellPrice = Math.floor((item.cost || 0) / 4);
                      return (
                        <div
                          key={item.index}
                          className={`crafting-recipe-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => { setSelectedSalvageItem(item); setLastSalvageResult(null); }}
                        >
                          <div className="crafting-recipe-item-name">
                            {item.quality && <span style={{ color: getRarityColor(item) }}>● </span>}
                            <span style={{ color: getRarityColor(item), textShadow: getRarityGlow(item) }}>{item.name}</span>
                            {item.quantity > 1 && <span className="salvage-qty"> x{item.quantity}</span>}
                          </div>
                          <div className="crafting-recipe-item-meta">
                            <span className="crafting-recipe-level">{item.type}</span>
                            {statBits.length > 0 && <span className="salvage-stat-preview"> · {statBits.join(' · ')}</span>}
                            {sellPrice > 0 && <span className="salvage-sell-preview"> · 💰{sellPrice}gp</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Salvage Detail */}
              <div className="crafting-detail">
                {selectedSalvageItem ? (
                  <>
                    <div className="crafting-detail-header">
                      <h3 style={{ color: getRarityColor(selectedSalvageItem), textShadow: getRarityGlow(selectedSalvageItem) }}>{selectedSalvageItem.name}</h3>
                      <span className="crafting-detail-level">{selectedSalvageItem.type}</span>
                    </div>

                    <div className="crafting-detail-section">
                      <div className="crafting-section-title">Item Details</div>
                      <div className="salvage-item-info">
                        {/* Rarity */}
                        <div className="salvage-info-row">
                          <span className="salvage-label">Rarity:</span>
                          <span className="salvage-value" style={{ color: getRarityColor(selectedSalvageItem) }}>{getRarityLabel(selectedSalvageItem)}</span>
                        </div>
                        {/* Type */}
                        <div className="salvage-info-row">
                          <span className="salvage-label">Type:</span>
                          <span className="salvage-value">{(selectedSalvageItem.type || 'misc').toUpperCase()}</span>
                        </div>
                        {/* Description */}
                        {selectedSalvageItem.description && selectedSalvageItem.description !== 'Unknown item' && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Info:</span>
                            <span className="salvage-value" style={{ color: '#bbb', fontStyle: 'italic' }}>{selectedSalvageItem.description}</span>
                          </div>
                        )}
                        {/* Lore */}
                        {selectedSalvageItem.lore && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Lore:</span>
                            <span className="salvage-value" style={{ color: '#9b9', fontStyle: 'italic' }}>{selectedSalvageItem.lore}</span>
                          </div>
                        )}
                        {/* Quality */}
                        {selectedSalvageItem.quality && selectedSalvageItem.quality !== 'standard' && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Quality:</span>
                            <span className="salvage-value" style={{ color: QUALITY_COLORS[selectedSalvageItem.quality] || '#ccc' }}>
                              {selectedSalvageItem.quality}
                            </span>
                          </div>
                        )}
                        {/* Enhancement */}
                        {selectedSalvageItem.enhancement > 0 && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Enhanced:</span>
                            <span className="salvage-value" style={{ color: '#4caf50' }}>+{selectedSalvageItem.enhancement}</span>
                          </div>
                        )}
                        {/* Damage */}
                        {selectedSalvageItem.damage && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Damage:</span>
                            <span className="salvage-value" style={{ color: '#f0f0f0' }}>{selectedSalvageItem.damage}</span>
                          </div>
                        )}
                        {/* ATT */}
                        {selectedSalvageItem.att > 0 && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Attack:</span>
                            <span className="salvage-value">+{selectedSalvageItem.att}</span>
                          </div>
                        )}
                        {/* DEF */}
                        {selectedSalvageItem.bonus > 0 && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Defense:</span>
                            <span className="salvage-value">+{selectedSalvageItem.bonus}</span>
                          </div>
                        )}
                        {/* DR */}
                        {selectedSalvageItem.dr > 0 && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Dmg Reduction:</span>
                            <span className="salvage-value">{selectedSalvageItem.dr}</span>
                          </div>
                        )}
                        {/* Melee Type */}
                        {selectedSalvageItem.mtype && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Melee Type:</span>
                            <span className="salvage-value">{selectedSalvageItem.mtype}</span>
                          </div>
                        )}
                        {/* Elemental damage */}
                        {selectedSalvageItem.mdamage && selectedSalvageItem.mtype && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">{selectedSalvageItem.mtype} Dmg:</span>
                            <span className="salvage-value" style={{ color: '#ff9800' }}>{selectedSalvageItem.mdamage}</span>
                          </div>
                        )}
                        {/* Capacity */}
                        {selectedSalvageItem.size > 0 && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Capacity:</span>
                            <span className="salvage-value" style={{ color: '#d4a017' }}>+{selectedSalvageItem.size} slots</span>
                          </div>
                        )}
                        {/* Sockets */}
                        {selectedSalvageItem.sockets && selectedSalvageItem.sockets.length > 0 && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Gems:</span>
                            <span className="salvage-value" style={{ color: '#e91e63' }}>
                              {selectedSalvageItem.sockets.filter(s => s).length}/{selectedSalvageItem.sockets.length} socketed
                            </span>
                          </div>
                        )}
                        {/* Enchantments */}
                        {selectedSalvageItem.enchantments && selectedSalvageItem.enchantments.length > 0 && (
                          selectedSalvageItem.enchantments.map((ench, i) => (
                            <div key={`ench-${i}`} className="salvage-info-row">
                              <span className="salvage-label">{i === 0 ? 'Enchant:' : ''}</span>
                              <span className="salvage-value" style={{ color: '#ffb74d' }}>📜 {ench.label}</span>
                            </div>
                          ))
                        )}
                        {/* Magic bonuses */}
                        {selectedSalvageItem.magic && normalizeMagic(selectedSalvageItem.magic).length > 0 && (
                          normalizeMagic(selectedSalvageItem.magic).map((m, i) => (
                            <div key={`mag-${i}`} className="salvage-info-row">
                              <span className="salvage-label">{i === 0 ? 'Magic:' : ''}</span>
                              <span className="salvage-value" style={{ color: '#bf7fff' }}>
                                +{m.mod || m.bonus || 0} {m.type === 'ablup' ? (m.abl || '').toUpperCase() : m.type.toUpperCase()}
                              </span>
                            </div>
                          ))
                        )}
                        {/* Level req */}
                        {selectedSalvageItem.level > 1 && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Requires:</span>
                            <span className="salvage-value" style={{ color: '#c66' }}>Lv {selectedSalvageItem.level}</span>
                          </div>
                        )}
                        {selectedSalvageItem.crafted && (
                          <div className="salvage-info-row">
                            <span className="salvage-label">Origin:</span>
                            <span className="salvage-value">⚒️ Player Crafted</span>
                          </div>
                        )}
                        {/* Value + Sell price */}
                        <div className="salvage-info-row" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 4, paddingTop: 6 }}>
                          <span className="salvage-label">Value:</span>
                          <span className="salvage-value">💰 {selectedSalvageItem.cost || 0} gp <span style={{ color: '#aaa' }}>(sell: {Math.floor((selectedSalvageItem.cost || 0) / 4)} gp)</span></span>
                        </div>
                      </div>
                    </div>

                    <div className="crafting-detail-section">
                      <div className="crafting-section-title">Expected Salvage</div>
                      <div className="salvage-expected">
                        <div className="salvage-expected-text">
                          🔨 {(selectedSalvageItem.cost || 0) <= 60 ? '1-3' : (selectedSalvageItem.cost || 0) <= 200 ? '1-4' : '2-5'} crafting materials
                          {selectedSalvageItem.magic && (Array.isArray(selectedSalvageItem.magic) ? selectedSalvageItem.magic.length : Object.keys(selectedSalvageItem.magic).length) > 0 && ' + gem material'}
                        </div>
                        <div className="salvage-expected-xp">
                          +{Math.max(5, Math.floor(((selectedSalvageItem.cost || 10)) / 5))} {['restore', 'potion', 'elixir', 'oil'].includes(selectedSalvageItem.type) ? 'alchemy' : 'smithing'} XP
                        </div>
                        <div className="salvage-warning">
                          ⚠️ This will destroy the item permanently
                        </div>
                      </div>
                    </div>

                    {/* Salvage Result */}
                    {lastSalvageResult && (
                      <div className="crafting-result salvage-result-box">
                        <div className="crafting-result-title">
                          <span style={{ color: '#4fc3f7' }}>🔨 Salvage Complete!</span>
                        </div>
                        <div className="salvage-materials-list">
                          {lastSalvageResult.materials.map((mat, i) => (
                            <div key={i} className="salvage-material-gained">
                              <span className="salvage-material-icon">📦</span>
                              <span>{mat.qty}x {mat.name}</span>
                            </div>
                          ))}
                        </div>
                        <div className="salvage-xp-gained">+{lastSalvageResult.xpGained} {lastSalvageResult.discipline || 'smithing'} XP</div>
                        {lastSalvageResult.discoveredRecipeName && (
                          <div className="salvage-discovery">
                            📜 Recipe Discovered: <strong>{lastSalvageResult.discoveredRecipeName}</strong>!
                          </div>
                        )}
                      </div>
                    )}

                    {/* Salvage Button */}
                    <button
                      className="crafting-craft-btn ready salvage-btn"
                      disabled={salvaging}
                      onClick={() => setShowSalvageConfirm(true)}
                    >
                      {salvaging ? '⏳ Salvaging...' : `🔨 Salvage ${selectedSalvageItem.name}`}
                    </button>

                    {/* Salvage Confirmation Modal */}
                    {showSalvageConfirm && (
                      <div className="salvage-confirm-overlay" onClick={() => setShowSalvageConfirm(false)}>
                        <div className="salvage-confirm-box" onClick={e => e.stopPropagation()}>
                          <h3>🔨 Confirm Salvage</h3>
                          <p>Salvage <strong style={{ color: getRarityColor(selectedSalvageItem.rarity) }}>{selectedSalvageItem.name}</strong>?</p>
                          <p className="salvage-confirm-warning">⚠️ This will destroy the item permanently.</p>
                          <div className="salvage-confirm-btns">
                            <button className="salvage-confirm-cancel" onClick={() => setShowSalvageConfirm(false)}>Cancel</button>
                            <button className="salvage-confirm-ok" onClick={handleSalvage}>Salvage</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="crafting-detail-empty">
                    <div className="crafting-detail-empty-icon">🔨</div>
                    <div>Select an item to salvage</div>
                    <div className="crafting-detail-empty-hint">
                      Break down equipment into crafting materials. Higher smithing level = better returns.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── SOCKET TAB ── */}
        {mainTab === 'socket' && (
          <>
            {/* Socket Info Bar */}
            <div className="crafting-xp-bar-container socket-info-bar">
              <div className="crafting-xp-label">
                💎 Gem Socketing — Insert gems into equipment sockets
                <span className="crafting-xp-text">
                  Enchanting Lv.{socketData?.enchantingLevel || 0} (Requires Lv.3)
                </span>
              </div>
            </div>

            {/* Socket Body */}
            <div className="crafting-body">
              {/* Equipped Items List */}
              <div className="crafting-recipe-list">
                <div className="crafting-recipe-list-header">🛡️ Equipped Items ({socketData?.items?.length || 0})</div>
                {socketLoading ? (
                  <div className="crafting-loading">Loading equipment...</div>
                ) : !socketData?.items?.length ? (
                  <div className="crafting-empty">No socketable equipment found</div>
                ) : (
                  <div className="crafting-recipe-scroll">
                    {socketData.items.map((item) => {
                      const isSelected = selectedSocketItem?.slot === item.slot;
                      const socketsUsed = item.usedSockets || 0;
                      const socketsMax = item.maxSockets || 0;
                      return (
                        <div
                          key={item.slot}
                          className={`crafting-recipe-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => { setSelectedSocketItem(item); setSelectedGem(null); setLastSocketResult(null); }}
                        >
                          <div className="crafting-recipe-item-name">
                            {item.quality && <span style={{ color: QUALITY_COLORS[item.quality] || '#ccc' }}>● </span>}
                            {item.name}
                          </div>
                          <div className="crafting-recipe-item-meta">
                            <span className="socket-slot-count">
                              💎 {socketsUsed}/{socketsMax}
                            </span>
                            <span className="crafting-recipe-level">{item.slot}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Socket Detail */}
              <div className="crafting-detail">
                {selectedSocketItem ? (
                  <>
                    <div className="crafting-detail-header">
                      <h3>{selectedSocketItem.name}</h3>
                      <span className="crafting-detail-level">{selectedSocketItem.slot}</span>
                    </div>

                    {/* Current Sockets Display */}
                    <div className="crafting-detail-section">
                      <div className="crafting-section-title">
                        Socket Slots ({selectedSocketItem.usedSockets || 0}/{selectedSocketItem.maxSockets || 0})
                      </div>
                      <div className="socket-slots-display">
                        {Array.from({ length: selectedSocketItem.maxSockets || 0 }).map((_, i) => {
                          const socketEntry = selectedSocketItem.sockets?.[i];
                          const bonusType = socketEntry?.bonus?.type || socketEntry?.type;
                          const bonusMod = socketEntry?.bonus?.mod || socketEntry?.mod;
                          return (
                            <div key={i} className={`socket-slot ${socketEntry ? 'filled' : 'empty'}`}>
                              {socketEntry ? (
                                <span className="socket-gem-name">💎 {socketEntry.gem} — +{bonusMod} {(bonusType || '').toUpperCase()}</span>
                              ) : (
                                <span className="socket-empty-label">🔲 Empty Socket</span>
                              )}
                            </div>
                          );
                        })}
                        {(selectedSocketItem.maxSockets || 0) === 0 && (
                          <div className="socket-no-slots">This item has no sockets. Only Fine+ quality items have sockets.</div>
                        )}
                      </div>
                    </div>

                    {/* Available Gems */}
                    {(selectedSocketItem.usedSockets || 0) < (selectedSocketItem.maxSockets || 0) && (
                      <div className="crafting-detail-section">
                        <div className="crafting-section-title">Available Gems ({socketData?.gems?.length || 0})</div>
                        {socketData?.gems?.length > 0 ? (
                          <div className="socket-gem-list">
                            {socketData.gems.map((gem, i) => {
                              const isGemSelected = selectedGem?.index === gem.index;
                              return (
                                <div
                                  key={gem.index}
                                  className={`socket-gem-option ${isGemSelected ? 'selected' : ''}`}
                                  onClick={() => { setSelectedGem(gem); setLastSocketResult(null); }}
                                >
                                  <span className="socket-gem-icon">💎</span>
                                  <span className="socket-gem-label">{gem.name}</span>
                                  <span className="socket-gem-bonus">{gem.bonus?.label}</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="crafting-empty">No gems in inventory</div>
                        )}
                      </div>
                    )}

                    {/* Socket Result */}
                    {lastSocketResult && (
                      <div className="crafting-result socket-result-box">
                        <div className="crafting-result-title">
                          <span style={{ color: '#ce93d8' }}>💎 Gem Socketed!</span>
                        </div>
                        <div className="socket-result-detail">
                          {lastSocketResult.gemName} → {lastSocketResult.itemName}
                        </div>
                        <div className="socket-result-bonus">
                          {lastSocketResult.bonus?.label}
                        </div>
                        <div className="salvage-xp-gained">+{lastSocketResult.xpGained} enchanting XP</div>
                      </div>
                    )}

                    {/* Socket Button */}
                    <button
                      className="crafting-craft-btn ready socket-btn"
                      disabled={
                        socketing ||
                        !selectedGem ||
                        (selectedSocketItem.usedSockets || 0) >= (selectedSocketItem.maxSockets || 0) ||
                        (socketData?.enchantingLevel || 0) < 3
                      }
                      onClick={handleSocket}
                    >
                      {socketing
                        ? '⏳ Socketing...'
                        : (socketData?.enchantingLevel || 0) < 3
                          ? '🔒 Requires Enchanting Lv.3'
                          : !selectedGem
                            ? '💎 Select a Gem'
                            : `💎 Socket ${selectedGem.name}`}
                    </button>
                  </>
                ) : (
                  <div className="crafting-detail-empty">
                    <div className="crafting-detail-empty-icon">💎</div>
                    <div>Select an equipped item to socket</div>
                    <div className="crafting-detail-empty-hint">
                      Insert gems into equipment sockets for permanent stat bonuses.
                      Higher quality items have more sockets.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── ENHANCE TAB ── */}
        {mainTab === 'enhance' && (
          <>
            {/* Enhance Info Bar */}
            <div className="crafting-xp-bar-container enhance-info-bar">
              <div className="crafting-xp-label">
                ⬆️ Enhancement — Upgrade equipment with materials and gold
                <span className="crafting-xp-text">
                  Smithing Lv.{enhanceData?.smithingLevel || 0} | 💰 {enhanceData?.gold || 0}
                </span>
              </div>
            </div>

            {/* Enhance Body */}
            <div className="crafting-body">
              {/* Equipped Items List */}
              <div className="crafting-recipe-list">
                <div className="crafting-recipe-list-header">🛡️ Equipped Items ({enhanceData?.items?.length || 0})</div>
                {enhanceLoading ? (
                  <div className="crafting-loading">Loading equipment...</div>
                ) : !enhanceData?.items?.length ? (
                  <div className="crafting-empty">No enhanceable equipment found</div>
                ) : (
                  <div className="crafting-recipe-scroll">
                    {enhanceData.items.map((item) => {
                      const isSelected = selectedEnhanceItem?.slot === item.slot;
                      const enh = item.enhancement || 0;
                      return (
                        <div
                          key={item.slot}
                          className={`crafting-recipe-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => { setSelectedEnhanceItem(item); setLastEnhanceResult(null); }}
                        >
                          <div className="crafting-recipe-item-name">
                            {item.quality && <span style={{ color: QUALITY_COLORS[item.quality] || '#ccc' }}>● </span>}
                            {item.name}
                            {enh > 0 && <span className="enhance-level-badge">+{enh}</span>}
                          </div>
                          <div className="crafting-recipe-item-meta">
                            {enh < 5 ? (
                              <span className="enhance-next">→ +{enh + 1}</span>
                            ) : (
                              <span className="enhance-maxed">MAX</span>
                            )}
                            <span className="crafting-recipe-level">{item.slot}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Enhance Detail */}
              <div className="crafting-detail">
                {selectedEnhanceItem ? (
                  <>
                    <div className="crafting-detail-header">
                      <h3>
                        {selectedEnhanceItem.name}
                        {(selectedEnhanceItem.enhancement || 0) > 0 && (
                          <span className="enhance-current-badge">+{selectedEnhanceItem.enhancement}</span>
                        )}
                      </h3>
                      <span className="crafting-detail-level">{selectedEnhanceItem.slot}</span>
                    </div>

                    {(selectedEnhanceItem.enhancement || 0) >= 5 ? (
                      <div className="crafting-detail-section">
                        <div className="enhance-maxed-display">
                          <div className="enhance-maxed-icon">⭐</div>
                          <div className="enhance-maxed-text">Maximum Enhancement Reached (+5)</div>
                          <div className="enhance-maxed-hint">This item cannot be enhanced further.</div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Enhancement Requirements */}
                        <div className="crafting-detail-section">
                          <div className="crafting-section-title">
                            Enhancement to +{(selectedEnhanceItem.enhancement || 0) + 1}
                          </div>
                          <div className="enhance-requirements">
                            {/* Success Rate */}
                            <div className="enhance-req-row">
                              <span className="enhance-req-label">Success Rate:</span>
                              <span className={`enhance-req-value ${
                                (selectedEnhanceItem.requirements?.successRate || 0) >= 75 ? 'rate-high' :
                                (selectedEnhanceItem.requirements?.successRate || 0) >= 50 ? 'rate-mid' : 'rate-low'
                              }`}>
                                {selectedEnhanceItem.requirements?.successRate || 0}%
                              </span>
                            </div>
                            {/* Gold Cost */}
                            <div className="enhance-req-row">
                              <span className="enhance-req-label">Gold:</span>
                              <span className={`enhance-req-value ${
                                (enhanceData?.gold || 0) >= (selectedEnhanceItem.requirements?.gold || 0) ? 'has-mat' : 'missing-mat'
                              }`}>
                                💰 {selectedEnhanceItem.requirements?.gold || 0}
                                {(enhanceData?.gold || 0) < (selectedEnhanceItem.requirements?.gold || 0) &&
                                  ` (have ${enhanceData?.gold || 0})`}
                              </span>
                            </div>
                            {/* Smithing Level */}
                            <div className="enhance-req-row">
                              <span className="enhance-req-label">Smithing:</span>
                              <span className={`enhance-req-value ${
                                (enhanceData?.smithingLevel || 0) >= (selectedEnhanceItem.requirements?.requiredSmithingLevel || 0) ? 'has-mat' : 'missing-mat'
                              }`}>
                                Lv.{selectedEnhanceItem.requirements?.requiredSmithingLevel || 0} required
                                {(enhanceData?.smithingLevel || 0) < (selectedEnhanceItem.requirements?.requiredSmithingLevel || 0) &&
                                  ` (have Lv.${enhanceData?.smithingLevel || 0})`}
                              </span>
                            </div>
                            {/* Materials - list each required material */}
                            {selectedEnhanceItem.requirements?.materials?.length > 0 ? (
                              selectedEnhanceItem.requirements.materials.map((mat, mi) => (
                                <div className="enhance-req-row" key={mi}>
                                  <span className="enhance-req-label">{mi === 0 ? 'Materials:' : ''}</span>
                                  <span className={`enhance-req-value ${mat.enough ? 'has-mat' : 'missing-mat'}`}>
                                    📦 {mat.name} ×{mat.need}
                                    {!mat.enough && ` (have ${mat.have})`}
                                    {mat.haveStash > 0 && (
                                      <span className="stash-indicator" title="From stash">
                                        {' '}(🏠{mat.haveStash})
                                      </span>
                                    )}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div className="enhance-req-row">
                                <span className="enhance-req-label">Materials:</span>
                                <span className="enhance-req-value has-mat">None required</span>
                              </div>
                            )}
                            {/* Gems (if needed) */}
                            {(selectedEnhanceItem.requirements?.gemCost || 0) > 0 && (
                              <div className="enhance-req-row">
                                <span className="enhance-req-label">Gems:</span>
                                <span className={`enhance-req-value ${
                                  selectedEnhanceItem.requirements?.hasGems ? 'has-mat' : 'missing-mat'
                                }`}>
                                  💎 {selectedEnhanceItem.requirements?.gemCost || 0} gem(s)
                                  {!selectedEnhanceItem.requirements?.hasGems &&
                                    ` (have ${selectedEnhanceItem.requirements?.gemsHave || 0})`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Enhancement Bonus Preview */}
                        <div className="crafting-detail-section">
                          <div className="crafting-section-title">Bonus Preview</div>
                          <div className="enhance-bonus-preview">
                            {selectedEnhanceItem.type === 'weapon' ? (
                              <div className="enhance-bonus-text">
                                ⚔️ +1 Damage Modifier, +1 ATT
                              </div>
                            ) : (
                              <div className="enhance-bonus-text">
                                🛡️ +1 DEF
                              </div>
                            )}
                            <div className="enhance-warning">
                              ⚠️ On failure: materials consumed, item unchanged
                            </div>
                          </div>
                        </div>

                        {/* Enhance Result */}
                        {lastEnhanceResult && (
                          <div className={`crafting-result enhance-result-box ${lastEnhanceResult.succeeded ? '' : 'enhance-failed'}`}>
                            <div className="crafting-result-title">
                              {lastEnhanceResult.succeeded ? (
                                <span style={{ color: '#66bb6a' }}>⬆️ Enhancement Success!</span>
                              ) : (
                                <span style={{ color: '#ef5350' }}>❌ Enhancement Failed</span>
                              )}
                            </div>
                            {lastEnhanceResult.succeeded ? (
                              <>
                                <div className="enhance-result-detail">
                                  {lastEnhanceResult.itemName} → +{lastEnhanceResult.newLevel}
                                </div>
                                <div className="salvage-xp-gained">+{lastEnhanceResult.xpGained} smithing XP</div>
                              </>
                            ) : (
                              <div className="enhance-result-detail">
                                Materials consumed. Item unchanged.
                                <br />+{lastEnhanceResult.xpGained} smithing XP (consolation)
                              </div>
                            )}
                          </div>
                        )}

                        {/* Enhance Button */}
                        <button
                          className={`crafting-craft-btn ready enhance-btn ${
                            selectedEnhanceItem.requirements?.canEnhance ? '' : 'disabled-btn'
                          }`}
                          disabled={enhancing || !selectedEnhanceItem.requirements?.canEnhance}
                          onClick={handleEnhance}
                        >
                          {enhancing
                            ? '⏳ Enhancing...'
                            : !selectedEnhanceItem.requirements?.canEnhance
                              ? '🔒 Requirements Not Met'
                              : `⬆️ Enhance to +${(selectedEnhanceItem.enhancement || 0) + 1} (${selectedEnhanceItem.requirements?.successRate}%)`}
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="crafting-detail-empty">
                    <div className="crafting-detail-empty-icon">⬆️</div>
                    <div>Select an equipped item to enhance</div>
                    <div className="crafting-detail-empty-hint">
                      Upgrade equipment up to +5 for stat bonuses.
                      Higher levels cost more and have lower success rates.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── SCROLLS TAB ── */}
        {mainTab === 'scrolls' && (
          <>
            {/* Scroll Info Bar */}
            <div className="crafting-xp-bar-container scroll-info-bar">
              <div className="crafting-xp-label">
                📜 Enchantment Scrolls — Apply magical enchantments to equipment
                <span className="crafting-xp-text">
                  Enchanting Lv.{scrollData?.enchantingLevel || 0} (Requires Lv.2)
                </span>
              </div>
            </div>

            {/* Scroll Body */}
            <div className="crafting-body">
              {/* Scroll List */}
              <div className="crafting-recipe-list">
                <div className="crafting-recipe-list-header">📜 Scrolls ({scrollData?.scrolls?.length || 0})</div>
                {scrollLoading ? (
                  <div className="crafting-loading">Loading scrolls...</div>
                ) : !scrollData?.scrolls?.length ? (
                  <div className="crafting-empty">No enchantment scrolls in inventory. Craft them via Enchanting recipes.</div>
                ) : (
                  <div className="crafting-recipe-scroll">
                    {scrollData.scrolls.map((scroll) => {
                      const isSelected = selectedScroll?.index === scroll.index;
                      return (
                        <div
                          key={scroll.index}
                          className={`crafting-recipe-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => { setSelectedScroll(scroll); setSelectedScrollTarget(null); setLastScrollResult(null); }}
                        >
                          <div className="crafting-recipe-item-name">
                            📜 {scroll.name}
                            {scroll.quantity > 1 && <span className="salvage-qty"> x{scroll.quantity}</span>}
                          </div>
                          <div className="crafting-recipe-item-meta">
                            <span className="scroll-effect-badge">{scroll.effect?.label}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Scroll Detail */}
              <div className="crafting-detail">
                {selectedScroll ? (
                  <>
                    <div className="crafting-detail-header">
                      <h3>{selectedScroll.name}</h3>
                      <span className="scroll-effect-label">{selectedScroll.effect?.label}</span>
                    </div>

                    {/* Scroll Effect Info */}
                    <div className="crafting-detail-section">
                      <div className="crafting-section-title">Enchantment Effect</div>
                      <div className="scroll-effect-info">
                        <div className="scroll-effect-row">
                          <span className="scroll-effect-icon">✨</span>
                          <span className="scroll-effect-text">{selectedScroll.effect?.label}</span>
                        </div>
                        <div className="scroll-effect-row">
                          <span className="scroll-effect-icon">🎯</span>
                          <span className="scroll-effect-text">
                            Target: {selectedScroll.effect?.target === 'weapon' ? 'Weapons Only' : 'Armor Only'}
                          </span>
                        </div>
                        {selectedScroll.effect?.mtype && (
                          <div className="scroll-effect-row">
                            <span className="scroll-effect-icon">
                              {selectedScroll.effect.mtype === 'fire' ? '🔥' :
                               selectedScroll.effect.mtype === 'cold' ? '❄️' :
                               selectedScroll.effect.mtype === 'lightning' ? '⚡' :
                               selectedScroll.effect.mtype === 'holy' ? '✨' : '🔮'}
                            </span>
                            <span className="scroll-effect-text">
                              {selectedScroll.effect.mtype.charAt(0).toUpperCase() + selectedScroll.effect.mtype.slice(1)} elemental damage
                            </span>
                          </div>
                        )}
                        <div className="scroll-warning">
                          ⚠️ Elemental enchantments will overwrite existing elemental effects
                        </div>
                      </div>
                    </div>

                    {/* Target Equipment Selection */}
                    <div className="crafting-detail-section">
                      <div className="crafting-section-title">Select Target Equipment</div>
                      <div className="scroll-target-list">
                        {scrollData?.items?.filter(item =>
                          (selectedScroll.effect?.target === 'weapon' && item.isWeapon) ||
                          (selectedScroll.effect?.target === 'armor' && item.isArmor)
                        ).length === 0 ? (
                          <div className="crafting-empty">
                            No compatible equipment found. {selectedScroll.effect?.target === 'weapon'
                              ? 'Equip a weapon first.' : 'Equip armor first.'}
                          </div>
                        ) : (
                          scrollData.items
                            .filter(item =>
                              (selectedScroll.effect?.target === 'weapon' && item.isWeapon) ||
                              (selectedScroll.effect?.target === 'armor' && item.isArmor)
                            )
                            .map(item => {
                              const isTarget = selectedScrollTarget?.slot === item.slot;
                              return (
                                <div
                                  key={item.slot}
                                  className={`scroll-target-option ${isTarget ? 'selected' : ''}`}
                                  onClick={() => { setSelectedScrollTarget(item); setLastScrollResult(null); }}
                                >
                                  <div className="scroll-target-name">
                                    {item.quality && <span style={{ color: QUALITY_COLORS[item.quality] || '#ccc' }}>● </span>}
                                    {item.name}
                                    {item.enhancement > 0 && <span className="enhance-level-badge">+{item.enhancement}</span>}
                                  </div>
                                  <div className="scroll-target-meta">
                                    <span className="scroll-target-slot">{item.slot}</span>
                                    {item.enchantments?.length > 0 && (
                                      <span className="scroll-enchant-count">
                                        ✨ {item.enchantments.length} enchant{item.enchantments.length > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                  {item.enchantments?.length > 0 && (
                                    <div className="scroll-existing-enchants">
                                      {item.enchantments.map((e, i) => (
                                        <span key={i} className="scroll-enchant-tag">{e.label}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                        )}
                      </div>
                    </div>

                    {/* Scroll Result */}
                    {lastScrollResult && (
                      <div className="crafting-result scroll-result-box">
                        <div className="crafting-result-title">
                          <span style={{ color: '#ffb74d' }}>📜 Enchantment Applied!</span>
                        </div>
                        <div className="scroll-result-detail">
                          {lastScrollResult.effect?.label}
                        </div>
                        <div className="salvage-xp-gained">+{lastScrollResult.xpGained} enchanting XP</div>
                      </div>
                    )}

                    {/* Apply Scroll Button */}
                    <button
                      className="crafting-craft-btn ready scroll-apply-btn"
                      disabled={
                        applyingScroll ||
                        !selectedScrollTarget ||
                        (scrollData?.enchantingLevel || 0) < 2
                      }
                      onClick={handleApplyScroll}
                    >
                      {applyingScroll
                        ? '⏳ Applying...'
                        : (scrollData?.enchantingLevel || 0) < 2
                          ? '🔒 Requires Enchanting Lv.2'
                          : !selectedScrollTarget
                            ? '📜 Select a Target'
                            : `📜 Apply ${selectedScroll.name}`}
                    </button>
                  </>
                ) : (
                  <div className="crafting-detail-empty">
                    <div className="crafting-detail-empty-icon">📜</div>
                    <div>Select an enchantment scroll to apply</div>
                    <div className="crafting-detail-empty-hint">
                      Craft enchantment scrolls through Enchanting recipes,
                      then apply them to your equipped gear for permanent bonuses.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Message Bar */}
        {message.text && (
          <div className={`crafting-message ${message.type}`}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default CraftingPanel;
